import { useEffect, useRef, useState } from 'react';
import { useUpdateMerchant } from '../merchants/hooks/useMerchants';
import type { Merchant, InvoicingCadence, LateFeeType } from '../merchants/types';
import { INVOICING_CADENCE_LABELS, LATE_FEE_TYPE_LABELS } from '../merchants/types';
import { PLATFORM_DEFAULT_LATE_FEE_GRACE_PERIOD_DAYS, PLATFORM_DEFAULT_PRICE_EXPIRY_DAYS } from './lib/billing';
import Button from '../../shared/components/Button';

const fieldCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

interface Props {
  merchant: Merchant;
  canUpdate: boolean;
}

/**
 * Per-merchant billing overrides (PE-2105): invoicing cadence, price-expiry window, and the
 * late-payment penalty rule. Every field falls back to a platform default (shown as a
 * placeholder) when unset on the merchant — clearing a field back to blank reverts to that
 * default rather than sending an explicit zero.
 *
 * No platform-wide defaults endpoint exists in this ticket's backend contract (only
 * `merchants PATCH /:id`), so the "platform default" side is a client-side constant — flagged
 * as a residual risk to confirm against watchman once a platform-settings endpoint exists.
 */
export function BillingSettingsPanel({ merchant, canUpdate }: Props) {
  const updateMerchant = useUpdateMerchant();

  const [invoicingCadence, setInvoicingCadence] = useState<InvoicingCadence>(merchant.invoicingCadence);
  const [priceExpiryDays, setPriceExpiryDays] = useState(
    merchant.invoicePriceExpiryDays != null ? String(merchant.invoicePriceExpiryDays) : '',
  );
  const [lateFeeType, setLateFeeType] = useState<LateFeeType | ''>(merchant.lateFeeType ?? '');
  const [lateFeeAmount, setLateFeeAmount] = useState(
    merchant.lateFeeAmount != null ? String(merchant.lateFeeAmount) : '',
  );
  const [lateFeeGracePeriodDays, setLateFeeGracePeriodDays] = useState(
    merchant.lateFeeGracePeriodDays != null ? String(merchant.lateFeeGracePeriodDays) : '',
  );

  // Re-seed local state if a different merchant is passed in (component instance is reused
  // across the merchant list rather than remounted).
  const hydratedFor = useRef(merchant.id);
  useEffect(() => {
    if (hydratedFor.current !== merchant.id) {
      hydratedFor.current = merchant.id;
      setInvoicingCadence(merchant.invoicingCadence);
      setPriceExpiryDays(merchant.invoicePriceExpiryDays != null ? String(merchant.invoicePriceExpiryDays) : '');
      setLateFeeType(merchant.lateFeeType ?? '');
      setLateFeeAmount(merchant.lateFeeAmount != null ? String(merchant.lateFeeAmount) : '');
      setLateFeeGracePeriodDays(merchant.lateFeeGracePeriodDays != null ? String(merchant.lateFeeGracePeriodDays) : '');
    }
  }, [merchant]);

  const isDirty =
    invoicingCadence !== merchant.invoicingCadence ||
    priceExpiryDays !== (merchant.invoicePriceExpiryDays != null ? String(merchant.invoicePriceExpiryDays) : '') ||
    lateFeeType !== (merchant.lateFeeType ?? '') ||
    lateFeeAmount !== (merchant.lateFeeAmount != null ? String(merchant.lateFeeAmount) : '') ||
    lateFeeGracePeriodDays !== (merchant.lateFeeGracePeriodDays != null ? String(merchant.lateFeeGracePeriodDays) : '');

  const handleSave = () => {
    if (!isDirty) return;
    updateMerchant.mutate({
      id: merchant.id,
      data: {
        invoicingCadence,
        invoicePriceExpiryDays: priceExpiryDays.trim() === '' ? null : Number(priceExpiryDays),
        lateFeeType: lateFeeType === '' ? null : lateFeeType,
        lateFeeAmount: lateFeeAmount.trim() === '' ? null : Number(lateFeeAmount),
        lateFeeGracePeriodDays: lateFeeGracePeriodDays.trim() === '' ? null : Number(lateFeeGracePeriodDays),
      },
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-foreground">Billing Settings</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Invoicing cadence, how long a pending invoice's price stays valid, and the late-payment
          penalty applied once an invoice is overdue past its grace period. Leave a field blank to
          use the platform default.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Invoicing Cadence</label>
          <select
            className={fieldCls}
            value={invoicingCadence}
            onChange={(e) => setInvoicingCadence(e.target.value as InvoicingCadence)}
            disabled={!canUpdate}
          >
            {(Object.keys(INVOICING_CADENCE_LABELS) as InvoicingCadence[]).map((c) => (
              <option key={c} value={c}>{INVOICING_CADENCE_LABELS[c]}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Price-Expiry Window (days)</label>
          <input
            type="number"
            min="1"
            className={fieldCls}
            placeholder={`Platform default: ${PLATFORM_DEFAULT_PRICE_EXPIRY_DAYS}`}
            value={priceExpiryDays}
            onChange={(e) => setPriceExpiryDays(e.target.value)}
            disabled={!canUpdate}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Late Fee Type</label>
          <select
            className={fieldCls}
            value={lateFeeType}
            onChange={(e) => setLateFeeType(e.target.value as LateFeeType | '')}
            disabled={!canUpdate}
          >
            <option value="">No late fee</option>
            {(Object.keys(LATE_FEE_TYPE_LABELS) as LateFeeType[]).map((t) => (
              <option key={t} value={t}>{LATE_FEE_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Late Fee Amount {lateFeeType === 'PERCENTAGE' ? '(basis points, e.g. 150 = 1.5%)' : '(₦, minor units)'}
          </label>
          <input
            type="number"
            min="0"
            step="1"
            className={fieldCls}
            value={lateFeeAmount}
            onChange={(e) => setLateFeeAmount(e.target.value)}
            disabled={!canUpdate || !lateFeeType}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Late Fee Grace Period (days)</label>
          <input
            type="number"
            min="0"
            className={fieldCls}
            placeholder={`Platform default: ${PLATFORM_DEFAULT_LATE_FEE_GRACE_PERIOD_DAYS}`}
            value={lateFeeGracePeriodDays}
            onChange={(e) => setLateFeeGracePeriodDays(e.target.value)}
            disabled={!canUpdate}
          />
        </div>
      </div>

      {canUpdate && (
        <div className="flex justify-end mt-4">
          <Button onClick={handleSave} disabled={!isDirty || updateMerchant.isPending} loading={updateMerchant.isPending}>
            Save Billing Settings
          </Button>
        </div>
      )}
    </div>
  );
}
